import React, { useState, useRef, useMemo } from 'react';
import { debounce } from 'lodash';

import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { DetailFormValue } from 'components/DetailsView';
import {
    GetDetailedTotemsOfOrganizationQuery,
    useGetPlaceAutocompleteSuggestionsLazyQuery,
    useGetPlaceDetailsLazyQuery,
} from 'data/__generated__';

type AutocompleteSuggestion = {
    label: string;
    reference: string;
};

export const AdresseInfoForm = ({
    totem,
}: {
    totem: GetDetailedTotemsOfOrganizationQuery['totemsOfOrganization'][number];
}) => {
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<AutocompleteSuggestion[]>([]);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const { register, setValue, setError, clearErrors } = useFormContext();

    const [getAutocompleteSuggestions] = useGetPlaceAutocompleteSuggestionsLazyQuery({
        onCompleted: (data) => {
            const suggestions = data.getPlaceAutocompleteSuggestions.map((suggestion) => ({
                label: suggestion.description,
                reference: suggestion.placeId,
            }));
            setAutocompleteSuggestions(suggestions);
        },
    });

    const [getPlaceDetails] = useGetPlaceDetailsLazyQuery();

    const debouncedGetAutocompleteSuggestions = useMemo(
        () =>
            debounce((value: string) => {
                getAutocompleteSuggestions({ variables: { input: value } });
            }, 1000),
        [getAutocompleteSuggestions],
    );

    const handleAddressSelection = (selectedSuggestion: AutocompleteSuggestion) => async () => {
        setAutocompleteOpen(false);

        try {
            const { data } = await getPlaceDetails({
                variables: { placeId: selectedSuggestion.reference },
            });

            if (!data?.getPlaceDetails) {
                setError('address', {
                    type: 'manual',
                    message: "Une erreur est survenue lors de la récupération des détails de l'adresse",
                });
                return;
            }

            const detail = data.getPlaceDetails;

            if (!detail.streetNumber) {
                setError('address', {
                    type: 'manual',
                    message: 'Veuillez choisir une adresse complète avec un numéro de rue',
                });
                return;
            }

            if (!detail.city || !detail.country || !detail.zipcode) {
                setError('address', {
                    type: 'manual',
                    message:
                        "L'adresse sélectionnée est incomplète. Veuillez sélectionner une adresse avec une ville, un code postal et un pays.",
                });
                return;
            }

            const address = detail.name || detail.formattedAddress || '';
            setValue('address', address);
            setValue('latitude', detail.latitude);
            setValue('longitude', detail.longitude);
            setValue('zipcode', detail.zipcode);
            setValue('city', detail.city);
            setValue('country', detail.country);

            if (inputRef.current) {
                inputRef.current.value = `${address}, ${detail.city ?? ''}, ${detail.country ?? ''}`;
            }
        } catch {
            setError('address', {
                type: 'manual',
                message: "Une erreur est survenue lors de la récupération des détails de l'adresse",
            });
        }
    };

    const handleAddressAutocompleteChange = ({ target: { value } }: { target: { value: string } }) => {
        if (value) {
            debouncedGetAutocompleteSuggestions(value);
            clearErrors('address');
            setAutocompleteOpen(true);
        } else {
            debouncedGetAutocompleteSuggestions.cancel();
            setAutocompleteOpen(false);
        }
    };

    const handleBlur = () => {
        // Délai pour permettre la sélection d'une suggestion avant la fermeture
        setTimeout(() => {
            setAutocompleteOpen(false);
        }, 200);
    };

    const handleFocus = () => {
        if (inputRef.current?.value) {
            setAutocompleteOpen(true);
        }
    };

    return (
        <Container>
            <DetailFormValue
                defaultValue={totem.address ? totem.address + ', ' + totem.city + ', ' + totem.country : ''}
                type="string"
                {...register('address', {
                    required: true,
                })}
                onChange={handleAddressAutocompleteChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                ref={inputRef}
                label="Adresse"
            />
            {autocompleteOpen && autocompleteSuggestions.length > 0 && (
                <SettingsAutocompleteContainer>
                    <SettingsAutocompleteItems>
                        {autocompleteSuggestions.map((suggestion, index) => (
                            <SettingsAutocompleteItem key={index} onMouseDown={handleAddressSelection(suggestion)}>
                                {suggestion.label}
                            </SettingsAutocompleteItem>
                        ))}
                    </SettingsAutocompleteItems>
                </SettingsAutocompleteContainer>
            )}
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;

const SettingsAutocompleteContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
`;

const SettingsAutocompleteItems = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 5px 0;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SettingsAutocompleteItem = styled.div`
    padding: 10px;
    cursor: pointer;
    &:hover {
        background-color: #f5f5f5;
    }
`;
